import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LoginPage.css'; // Changed to separate CSS file
import { loginUser, sendOtp, verifyOtp, resetPassword } from '../Service/authservice';

function Login() {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetErrors, setResetErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const validatePhoneNumber = (number) => /^\d{11}$/.test(number);
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Start countdown timer for OTP resend
  const startTimer = () => {
    setTimer(60); // 60 seconds
    setCanResend(false);
    
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be exactly 11 digits';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const data = await loginUser({ phoneNumber, password });

      if (data.code === 1) {
        const tenantId = data.additionalDetail.tenantId;
        const token = data.additionalDetail.token;
        const phone = data.additionalDetail.phoneNumber;
        const authorities = data.additionalDetail.authorities;
        const role = data.additionalDetail.role;

        localStorage.setItem('tenantId', tenantId);
        localStorage.setItem('sessionToken', token);
        localStorage.setItem('userPhone', phone);
                localStorage.setItem('userRole' , role); 
        console.log('Login successful, token saved:', token);
        const sessionDuration = 1 * 60 * 1000;
        localStorage.setItem('sessionExpiry', Date.now() + sessionDuration);
        localStorage.setItem('authorities', JSON.stringify(authorities));

        navigate('/dashboard');
      } else {
        alert(data.additionalDetail);
      }
    } catch (error) {
      console.error('Login Failed:', error);
      alert(error.message);
    }
  };

  const handleForgotPassword = () => {
    setShowResetModal(true);
    setResetStep(1);
    setEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResetErrors({});
    setResetMessage('');
    setTimer(0);
    setCanResend(true);
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetStep(1);
    setResetErrors({});
    setResetMessage('');
    setTimer(0);
    setCanResend(true);
  };

  const handleSendOtp = async () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setResetErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const response = await sendOtp({ email });
      if (response.code === 1) {
        setResetStep(2);
        setResetMessage('4-digit OTP sent to your email. Please check your inbox.');
        startTimer(); // Start the resend timer
      } else {
        setResetErrors({ email: response.additionalDetail || 'Failed to send OTP' });
      }
    } catch (error) {
      setResetErrors({ email: 'Error sending OTP. Please try again.' });
      console.error('OTP send error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const newErrors = {};
    if (!otp) {
      newErrors.otp = 'OTP is required';
    } else if (otp.length !== 4) { // Changed from 6 to 4
      newErrors.otp = 'OTP must be 4 digits';
    }

    setResetErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const response = await verifyOtp({ email, otp });
      if (response.code === 1) {
        setResetStep(3);
        setResetMessage('OTP verified successfully. Please set your new password.');
      } else {
        setResetErrors({ otp: response.additionalDetail || 'Invalid OTP' });
      }
    } catch (error) {
      setResetErrors({ otp: 'Error verifying OTP. Please try again.' });
      console.error('OTP verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const newErrors = {};
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setResetErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const response = await resetPassword({
        email,
        otp,
        newPassword,
        confirmPassword
      });
      
      if (response.code === 1) {
        setResetMessage('Password reset successfully! You can now login with your new password.');
        setTimeout(() => {
          handleCloseResetModal();
        }, 3000);
      } else {
        setResetErrors({ general: response.additionalDetail || 'Failed to reset password' });
      }
    } catch (error) {
      setResetErrors({ general: 'Error resetting password. Please try again.' });
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      const response = await sendOtp({ email });
      if (response.code === 1) {
        setResetMessage('New OTP sent to your email.');
        startTimer(); // Restart the timer
      } else {
        setResetErrors({ general: response.additionalDetail || 'Failed to resend OTP' });
      }
    } catch (error) {
      setResetErrors({ general: 'Error resending OTP. Please try again.' });
      console.error('Resend OTP error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderResetStep = () => {
    switch (resetStep) {
      case 1:
        return (
          <div className="reset-step">
            <h3>Forgot Password</h3>
            <p>Enter your registered email address to receive OTP</p>
            <div className="reset-input-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={resetErrors.email ? 'error-input' : ''}
              />
              {resetErrors.email && (
                <span className="error-message">{resetErrors.email}</span>
              )}
            </div>
            <button 
              type="button" 
              onClick={handleSendOtp}
              disabled={loading}
              className="reset-submit-btn"
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </div>
        );

      case 2:
        return (
          <div className="reset-step">
            <h3>Verify OTP</h3>
            <p>Enter the 4-digit OTP sent to your email</p> {/* Changed text */}
            <div className="reset-input-group">
              <label htmlFor="otp">OTP</label>
              <input
                id="otp"
                type="text"
                placeholder="Enter 4-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))} // Changed to 4
                className={resetErrors.otp ? 'error-input' : ''}
                maxLength="4" // Changed from 6 to 4
              />
              {resetErrors.otp && (
                <span className="error-message">{resetErrors.otp}</span>
              )}
            </div>
            <div className="reset-action-buttons">
              <button 
                type="button" 
                onClick={() => setResetStep(1)}
                className="reset-back-btn"
              >
                Back
              </button>
              <button 
                type="button" 
                onClick={handleVerifyOtp}
                disabled={loading}
                className="reset-submit-btn"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </div>
            <div className="resend-otp-section">
              {!canResend ? (
                <p className="resend-timer">
                  Resend OTP in {timer} seconds
                </p>
              ) : (
                <p className="resend-otp-link" onClick={handleResendOtp}>
                  Didn't receive OTP? <span>Resend</span>
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="reset-step">
            <h3>Set New Password</h3>
            <p>Create a new password for your account</p>
            <div className="reset-input-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={resetErrors.newPassword ? 'error-input' : ''}
              />
              {resetErrors.newPassword && (
                <span className="error-message">{resetErrors.newPassword}</span>
              )}
            </div>
            <div className="reset-input-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={resetErrors.confirmPassword ? 'error-input' : ''}
              />
              {resetErrors.confirmPassword && (
                <span className="error-message">{resetErrors.confirmPassword}</span>
              )}
            </div>
            {resetErrors.general && (
              <div className="error-message general-error">{resetErrors.general}</div>
            )}
            <div className="reset-action-buttons">
              <button 
                type="button" 
                onClick={() => setResetStep(2)}
                className="reset-back-btn"
              >
                Back
              </button>
              <button 
                type="button" 
                onClick={handleResetPassword}
                disabled={loading}
                className="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="login-page-container">
      {/* Background Layer with Company Name */}
      <div className="login-bg-layer">
        <div className="login-company-hero">
          <div className="login-company-name-large">AZ</div>
          <div className="login-system-name-large">MANAGEMENT SYSTEM</div>
          <div className="login-company-tagline">Streamline Your Business Operations</div>
          
          {/* Additional decorative elements */}
          <div className="login-decorative-line"></div>
          <div className="login-company-description">
            Professional business management solution
          </div>
        </div>
        
        {/* Subtle background pattern - optional */}
        <div className="bg-pattern"></div>
      </div>

      {/* Login Form Container */}
      <div className="login-form-overlay">
        <div className="login-card-container">
          <div className="login-form-header">
            <h3>Welcome Back</h3>
            <p>Sign in to continue</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="login-input-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                id="phoneNumber"
                type="text"
                placeholder="Enter 11-digit phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className={errors.phoneNumber ? 'login-error-input' : ''}
              />
              {errors.phoneNumber && (
                <span className="login-error-message">{errors.phoneNumber}</span>
              )}
            </div>

            <div className="login-input-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? 'login-error-input' : ''}
              />
              {errors.password && (
                <span className="login-error-message">{errors.password}</span>
              )}
            </div>

            <div className="login-forgot-password">
              <span onClick={handleForgotPassword} className="forgot-password-link">
                Forgot Password?
              </span>
            </div>

            <button type="submit" className="login-submit-btn">
              Sign In
            </button>
          </form>

          <div className="login-form-footer">
            <p>
              Don't have an account? <Link to="/signup">Sign Up</Link>
            </p>
            <p className="login-copyright">© 2024 AZ Management System</p>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="reset-modal-overlay">
          <div className="reset-modal-container">
            <button className="reset-modal-close" onClick={handleCloseResetModal}>
              &times;
            </button>
            
            {renderResetStep()}
            
            {resetMessage && (
              <div className={`reset-message ${resetStep === 3 ? 'success' : 'info'}`}>
                {resetMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;